import { readableColor } from 'color2k'

export const addOpacity = (color: string, opacity: number = 0.15): string => {
  const hexToRgb = (hex: string): number[] => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i
    hex = hex.replace(shorthandRegex, (_, r, g, b) => r + r + g + g + b + b)
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return result
      ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
      : [0, 0, 0]
  }

  const isHex = color.startsWith('#')
  const isRgb = color.startsWith('rgb')

  let r: number, g: number, b: number

  if (isHex) {
    ;[r, g, b] = hexToRgb(color)
  } else if (isRgb) {
    const match = color.match(/\d+/g)
    if (match) {
      ;[r, g, b] = match.map(Number)
    } else {
      throw new Error('Invalid RGB color format')
    }
  } else {
    throw new Error('Invalid color format. Use hex or RGB.')
  }

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}

export const blendWithWhite = (color: string, opacity: number = 0.15): string => {
  let r: number, g: number, b: number

  if (color.startsWith('#')) {
    let hex = color.slice(1)
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
    }
    r = parseInt(hex.substring(0, 2), 16)
    g = parseInt(hex.substring(2, 4), 16)
    b = parseInt(hex.substring(4, 6), 16)
  } else if (color.startsWith('rgb')) {
    const match = color.match(/\d+/g)
    if (match) {
      ;[r, g, b] = match.map(Number)
    } else {
      return color
    }
  } else {
    return color
  }

  const rB = Math.round(r * opacity + 255 * (1 - opacity))
  const gB = Math.round(g * opacity + 255 * (1 - opacity))
  const bB = Math.round(b * opacity + 255 * (1 - opacity))
  return `#${rB.toString(16).padStart(2, '0')}${gB.toString(16).padStart(2, '0')}${bB.toString(16).padStart(2, '0')}`
}

export const blendWithBlack = (color: string, opacity: number = 0.15): string => {
  let r: number, g: number, b: number

  if (color.startsWith('#')) {
    let hex = color.slice(1)
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2]
    }
    r = parseInt(hex.substring(0, 2), 16)
    g = parseInt(hex.substring(2, 4), 16)
    b = parseInt(hex.substring(4, 6), 16)
  } else if (color.startsWith('rgb')) {
    const match = color.match(/\d+/g)
    if (match) {
      ;[r, g, b] = match.map(Number)
    } else {
      return color
    }
  } else {
    return color
  }

  const rB = Math.round(r * (1 - opacity))
  const gB = Math.round(g * (1 - opacity))
  const bB = Math.round(b * (1 - opacity))
  return `#${rB.toString(16).padStart(2, '0')}${gB.toString(16).padStart(2, '0')}${bB.toString(16).padStart(2, '0')}`
}

// Returns black or white, whichever gives better contrast against the given background color
export const getContrastTextColor = (backgroundColor: string): string =>
  readableColor(backgroundColor)

// Colors may be stored with or without a leading '#'; these normalize between the two forms
export const ensureHash = (color: string): string => (color.startsWith('#') ? color : `#${color}`)
export const stripHash = (color: string): string => color.replace(/^#/, '').toUpperCase()

export interface CustomColorChipStyle {
  background: string
  text: string
}

// The standard "tag chip" treatment for an arbitrary custom color: a pale tint for the
// background, with black or white text chosen for contrast against that tint (not the raw color).
export const getCustomColorChipStyle = (color: string): CustomColorChipStyle => {
  const background = addOpacity(color, 0.15)
  let text: string
  try {
    text = getContrastTextColor(blendWithWhite(color, 0.15))
  } catch {
    text = '#000000'
  }
  return { background, text }
}

export const tagColors = [
  '#662D91',
  '#778899',
  '#F0AC42',
  '#39B54A',
  '#00ACEE',
  '#CBAE67',
  '#C49A6D',
  '#533D2F',
  '#ED1C24',
  '#E88A97',
  '#F28E1E',
  '#8DC63F',
  '#216C2A',
  '#D5A94C',
  '#FFE251',
  '#00A79D',
  '#52808B',
  '#ED1B33',
  '#142B55',
  '#E00686',
  '#505855',
  '#F7941E',
]
